import React, { useContext, useEffect, useState } from "react";
import { ContainerDiv, LoadingIcon } from "../../../../components";
import { useSearchParams } from "react-router-dom";
import { useLocalStorage } from "../../../../hooks";
import { UserContext } from "../../../../context/UserContext";
import { getCrops, getOrgs, getSites } from "../../../../utils/api";
import BroccoliVisualisationDashboard from "../broccoliGrowerDashboard/BroccoliVisualisationDashboard";
import SpinachVisualisationDashboard from "../spinachGrowerDashboard/SpinachVisualisationDashboard";
import DefaultVisualisationDashboard from "../defaultGrowerDashboard/DefaultVisualisationDashboard";

const GrowerVisualisationDashboard = () => {
  const { userData } = useContext(UserContext);

  const [orgData, setOrgData] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [orgLoading, setOrgLoading] = useState(false);

  const [siteData, setSiteData] = useState([]);
  const [currentSite, setCurrentSite] = useState(null);
  const [siteLoading, setSiteLoading] = useState(false);

  const [cropData, setCropData] = useState([]);
  const [currentCrop, setCurrentCrop] = useState(null);
  const [cropLoading, setCropLoading] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const [settingDefaults, setSettingDefaults] = useState({});
  const [dropdownLoading, setDropdownLoading] = useState(true);

  const [defaultDropdown, setDefaultDropdown] = useLocalStorage(
    "dropdowns",
    null,
  );

  // ----------------------------------------------------------------
  //  API fetchers
  // ----------------------------------------------------------------
  const fetchOrgs = async () => {
    setOrgLoading(true);
    try {
      const res = await getOrgs();
      const newData = res.data.data
        .filter((org) => org.is_active && org.is_grower)
        .map((org) => ({
          name: org.name,
          id: org.id,
        }));
      setOrgData(newData);
    } catch (e) {
      console.error("Error fetching Orgs:", e);
    } finally {
      setOrgLoading(false);
    }
  };

  const fetchSites = async (orgId) => {
    setSiteLoading(true);
    try {
      const res = await getSites(orgId);
      const newData = res.data.data
        .filter((s) => s.is_active)
        .map((s) => ({
          name: s.name,
          displayName: s.display_name,
          id: s.id,
          metadata: s.metadata,
          lat: s.lat,
          long: s.long,
        }));
      setSiteData(newData);
    } catch (e) {
      console.error("Error fetching Sites:", e);
    } finally {
      setSiteLoading(false);
    }
  };

  const fetchCrops = async (siteObj) => {
    if (!siteObj) return;
    setCropLoading(true);
    try {
      const res = await getCrops(siteObj.id);
      const newData = res?.data?.data
        .filter((c) => c.is_active)
        .map((c) => ({
          name: c.name,
          displayName: c.display_name,
          id: c.id,
        }));
      setCropData(newData);
    } catch (e) {
      console.error("Error fetching Crops:", e);
    } finally {
      setCropLoading(false);
    }
  };

  // ----------------------------------------------------------------
  //  On mount: superuser => fetch orgs; else set org from userData
  // ----------------------------------------------------------------
  useEffect(() => {
    const init = async () => {
      if (userData) {
        try {
          if (userData.is_superuser) {
            await fetchOrgs();
          } else {
            setCurrentOrg(userData.organization_id);

            const res = await getSites(userData.organization_id);
            const filtered = res?.data?.data?.filter((s) => s.is_active) || [];
            const mapped = filtered.map((s) => ({
              name: s.name,
              displayName: s.display_name,
              id: s.id,
              metadata: s.metadata,
              lat: s.lat,
              long: s.long,
            }));
            setSiteData(mapped);
          }
        } catch (err) {
          console.error("Error on mount:", err);
        }
      }
    };
    init();
  }, [userData]);

  // ----------------------------------------------------------------
  //  1) Org => parse from URL or localStorage or fallback
  // ----------------------------------------------------------------
  useEffect(() => {
    if (orgData?.length) {
      const params = new URLSearchParams(searchParams);
      const orgIdParam = params.get("org");
      if (orgIdParam) {
        const foundOrg = orgData.find((o) => o.id === orgIdParam);
        setCurrentOrg(foundOrg?.id ?? defaultDropdown?.org ?? orgData[0].id);
        setSettingDefaults((prev) => ({ ...prev, org: true }));
      } else if (!settingDefaults.org) {
        // localStorage
        setCurrentOrg(defaultDropdown?.org ?? orgData[0].id);
        setSettingDefaults((prev) => ({ ...prev, org: true }));
      } else {
        setCurrentOrg(orgData[0].id);
      }
    }
  }, [orgData]);

  // whenever currentOrg changes => store ID in localStorage + URL param
  useEffect(() => {
    if (currentOrg) {
      const params = new URLSearchParams(searchParams);
      params.set("org", currentOrg);
      setSearchParams(params);

      setDefaultDropdown({
        ...defaultDropdown,
        org: currentOrg, // store ID
      });
    }
  }, [currentOrg]);

  // If superuser => fetch sites on org change
  useEffect(() => {
    if (currentOrg && userData?.is_superuser) {
      fetchSites(currentOrg);
    }
  }, [currentOrg]);

  // ----------------------------------------------------------------
  //  2) Site => parse from URL or localStorage or fallback
  // ----------------------------------------------------------------
  useEffect(() => {
    if (siteData?.length && currentOrg) {
      const params = new URLSearchParams(searchParams);
      const siteIdParam = params.get("site");

      if (siteIdParam) {
        const foundSite = siteData.find((s) => s.id === siteIdParam);
        setCurrentSite(foundSite ?? siteData[0]);
      } else if (!settingDefaults.site) {
        const storedSiteId = defaultDropdown?.[currentOrg]?.site; // ID only
        if (storedSiteId) {
          const foundSite = siteData.find((s) => s.id === storedSiteId);
          setCurrentSite(foundSite ?? siteData[0]);
        } else {
          setCurrentSite(siteData[0]);
        }
        setSettingDefaults((prev) => ({ ...prev, site: true }));
      } else {
        setCurrentSite(siteData[0]);
      }
    }
  }, [siteData, currentOrg]);

  // whenever currentSite changes => localStorage + URL
  useEffect(() => {
    if (currentSite) {
      const params = new URLSearchParams(searchParams);
      params.set("site", currentSite.id);
      setSearchParams(params);

      const orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          site: currentSite.id, // store ID
        },
      });
    }
  }, [currentSite]);

  // Whenever site changes => fetch crops
  useEffect(() => {
    if (currentSite) {
      fetchCrops(currentSite);
    }
  }, [currentSite]);

  // ----------------------------------------------------------------
  //  3) Crop => parse from URL or localStorage or fallback
  // ----------------------------------------------------------------
  useEffect(() => {
    if (cropData?.length && currentSite) {
      const params = new URLSearchParams(searchParams);
      const cropIdParam = params.get("crop");

      if (cropIdParam) {
        const foundCrop = cropData.find((c) => c.id === cropIdParam);
        setCurrentCrop(foundCrop ?? cropData[0]);
      } else if (!settingDefaults.crop) {
        const storedCropId = defaultDropdown?.[currentOrg]?.crop;
        if (storedCropId) {
          const foundCrop = cropData.find((c) => c.id === storedCropId);
          setCurrentCrop(foundCrop ?? cropData[0]);
        } else {
          setCurrentCrop(cropData[0]);
        }
        setSettingDefaults((prev) => ({ ...prev, crop: true }));
      } else {
        setCurrentCrop(cropData[0]);
      }
    } else {
      setCurrentCrop(null);
    }
  }, [cropData, currentSite]);

  // whenever currentCrop changes => localStorage + URL
  useEffect(() => {
    if (currentCrop) {
      const params = new URLSearchParams(searchParams);
      params.set("crop", currentCrop.id);
      setSearchParams(params);

      const orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          crop: currentCrop.id, // store ID
        },
      });
    }
  }, [currentCrop]);

  // ----------------------------------------------------------------
  //  Dropdown loading
  // ----------------------------------------------------------------
  useEffect(() => {
    setDropdownLoading(orgLoading || siteLoading || cropLoading);
  }, [orgLoading, siteLoading, cropLoading, setDropdownLoading]);

  // ----------------------------------------------------------------
  //  Render logic
  // ----------------------------------------------------------------
  const renderDashboard = () => {
    if (!currentCrop || dropdownLoading) return null;
    if (currentCrop?.name === "broccoli") {
      return (
        <BroccoliVisualisationDashboard
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          setOrgData={setOrgData}
          orgLoading={orgLoading}
          setOrgLoading={setOrgLoading}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          siteData={siteData}
          setSiteData={setSiteData}
          siteLoading={siteLoading}
          setSiteLoading={setSiteLoading}
          currentCrop={currentCrop}
          setCurrentCrop={setCurrentCrop}
          cropData={cropData}
          setCropData={setCropData}
          cropLoading={cropLoading}
          setCropLoading={setCropLoading}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          settingDefaults={settingDefaults}
          setSettingDefaults={setSettingDefaults}
        />
      );
    } else if (currentCrop?.name === "spinach") {
      return (
        <SpinachVisualisationDashboard
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          setOrgData={setOrgData}
          orgLoading={orgLoading}
          setOrgLoading={setOrgLoading}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          siteData={siteData}
          setSiteData={setSiteData}
          siteLoading={siteLoading}
          setSiteLoading={setSiteLoading}
          currentCrop={currentCrop}
          setCurrentCrop={setCurrentCrop}
          cropData={cropData}
          setCropData={setCropData}
          cropLoading={cropLoading}
          setCropLoading={setCropLoading}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          settingDefaults={settingDefaults}
          setSettingDefaults={setSettingDefaults}
        />
      );
    }
    return (
      <DefaultVisualisationDashboard
        currentOrg={currentOrg}
        setCurrentOrg={setCurrentOrg}
        orgData={orgData}
        setOrgData={setOrgData}
        orgLoading={orgLoading}
        setOrgLoading={setOrgLoading}
        currentSite={currentSite}
        setCurrentSite={setCurrentSite}
        siteData={siteData}
        setSiteData={setSiteData}
        siteLoading={siteLoading}
        setSiteLoading={setSiteLoading}
        currentCrop={currentCrop}
        setCurrentCrop={setCurrentCrop}
        cropData={cropData}
        setCropData={setCropData}
        cropLoading={cropLoading}
        setCropLoading={setCropLoading}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        settingDefaults={settingDefaults}
        setSettingDefaults={setSettingDefaults}
      />
    );
  };

  return (
    <>
      {orgLoading || siteLoading || cropLoading ? (
        <LoadingIcon height="200px" classname="absolute h-full" />
      ) : (
        <ContainerDiv>{renderDashboard()}</ContainerDiv>
      )}
    </>
  );
};

export default GrowerVisualisationDashboard;
